import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  Activity, 
  Target, 
  Trophy, 
  TrendingUp, 
  Calendar,
  MapPin,
  Clock
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [periodLoading, setPeriodLoading] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  // Refetch data when period changes (for future dynamic loading)
  useEffect(() => {
    if (stats && selectedPeriod) {
      // Data is already loaded, just update the display
      // In future, you could implement dynamic loading here
      setPeriodLoading(false);
    }
  }, [selectedPeriod, stats]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setStats(null);
    } finally {
      setLoading(false);
      setPeriodLoading(false);
    }
  };

  const handlePeriodChange = (period) => {
    setPeriodLoading(true);
    setSelectedPeriod(period);
    // Simulate loading for smooth UX
    setTimeout(() => setPeriodLoading(false), 200);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Get the correct period stats
  const getPeriodStats = () => {
    if (!stats) return null;
    
    switch (selectedPeriod) {
      case 'today':
        return stats.todayStats;
      case 'week':
        return stats.weekStats;
      case 'month':
        return stats.monthStats;
      default:
        return stats.weekStats;
    }
  };

  const periodStats = getPeriodStats();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Dashboard Overview</h2>
        <p className="text-gray-600 mt-2">Welcome to your fitness app admin dashboard</p>
      </div>

      {/* Key Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Users</p>
              <p className="text-3xl font-bold text-white">{stats?.totalUsers || 0}</p>
            </div>
            <Users className="h-12 w-12 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Total Activities</p>
              <p className="text-3xl font-bold text-white">{stats?.totalActivities || 0}</p>
            </div>
            <Activity className="h-12 w-12 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">Active Goals</p>
              <p className="text-3xl font-bold text-white">{stats?.activeGoals || 0}</p>
            </div>
            <Target className="h-12 w-12 text-yellow-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Active Challenges</p>
              <p className="text-3xl font-bold text-white">{stats?.activeChallenges || 0}</p>
            </div>
            <Trophy className="h-12 w-12 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Period Selector and Stats */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Activity Statistics</h3>
          <div className="flex space-x-2">
            {['today', 'week', 'month'].map((period) => (
              <button
                key={period}
                onClick={() => handlePeriodChange(period)}
                disabled={periodLoading}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedPeriod === period
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } ${periodLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {periodLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <TrendingUp className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Total Steps</p>
              <p className="text-2xl font-bold text-gray-900">
                {(periodStats?.total_steps || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedPeriod === 'today' ? 'Today' : 
                 selectedPeriod === 'week' ? 'Last 7 days' : 
                 'Last 30 days'}
              </p>
            </div>

            <div className="text-center p-6 bg-green-50 rounded-lg">
              <MapPin className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Distance (km)</p>
              <p className="text-2xl font-bold text-gray-900">
                {(periodStats?.total_distance || 0).toFixed(1)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedPeriod === 'today' ? 'Today' : 
                 selectedPeriod === 'week' ? 'Last 7 days' : 
                 'Last 30 days'}
              </p>
            </div>

            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <Activity className="h-8 w-8 text-orange-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Calories Burned</p>
              <p className="text-2xl font-bold text-gray-900">
                {(periodStats?.total_calories || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedPeriod === 'today' ? 'Today' : 
                 selectedPeriod === 'week' ? 'Last 7 days' : 
                 'Last 30 days'}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Distribution by Gender */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Users by Gender</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats?.genderStats || []}
                  dataKey="count"
                  nameKey="gender"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {(stats?.genderStats || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Users by City */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Users by City</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.cityStats?.slice(0, 5) || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="city" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Performing Groups */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Groups (This Week)</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Members
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Steps
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Distance (km)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(stats?.topGroups || []).map((group, index) => (
                <tr key={index} className="table-row">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{group.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{group.city}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{group.member_count}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{(group.total_steps || 0).toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{(group.total_distance || 0).toFixed(1)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activities Feed */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
        <div className="space-y-4">
          {(stats?.recentActivities || []).slice(0, 5).map((activity, index) => (
            <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Activity className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {activity.user_name} - {activity.name || 'Activity'}
                </p>
                <p className="text-sm text-gray-500">
                  {activity.city} • {activity.steps} steps • {activity.distance_km}km
                </p>
              </div>
              <div className="flex-shrink-0 text-sm text-gray-400">
                <Clock className="h-4 w-4 inline mr-1" />
                {new Date(activity.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 